import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { adminApi } from '../services/api';

export default function AdminContactDetail() {
  const { contactId } = useParams();
  const { isAdmin } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.contactDetail(contactId)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [contactId]);

  const showToast = (t) => { setToast(t); setTimeout(() => setToast(null), 2000); };

  const resend = async (regen) => {
    if (!window.confirm(regen ? 'Regenerate token AND resend? The old link will stop working.' : 'Resend notification to this client?')) return;
    try { await adminApi.resendNotification(contactId, !!regen); showToast('Notification queued'); load(); } catch (err) { alert(err.message); }
  };
  const regen = async () => {
    if (!window.confirm('Regenerate token? The old link will stop working immediately.')) return;
    try { await adminApi.regenerateToken(contactId); showToast('Token regenerated'); load(); } catch (err) { alert(err.message); }
  };
  const forceComplete = async () => {
    const reason = window.prompt('Reason for force-complete (required):');
    if (!reason) return;
    try { await adminApi.forceComplete(contactId, reason); showToast('Marked complete'); load(); } catch (err) { alert(err.message); }
  };
  const markLender = async (lenderName) => {
    const reason = window.prompt(`Manually mark "${lenderName}" as sent. Reason:`);
    if (!reason) return;
    try { await adminApi.markLenderSent(contactId, lenderName, reason); showToast('Marked sent'); load(); } catch (err) { alert(err.message); }
  };

  if (loading) return <div className="card"><span className="spinner" /></div>;
  if (error) return <div className="card"><span className="badge danger">Error</span> <p className="muted">{error}</p></div>;
  if (!data) return null;

  const { contact, lenders, notifications, events } = data;

  return (
    <div className="stack-lg">
      <h1 className="h1">{contact.firstName} {contact.lastName}</h1>
      <div className="card stack">
        <div className="row wrap space">
          <div>
            <div className="muted small">Email</div>
            <div>{contact.emailMasked}</div>
          </div>
          <div>
            <div className="muted small">Stage</div>
            <div><span className="badge">{contact.stage}</span></div>
          </div>
          <div>
            <div className="muted small">Notification</div>
            <div><span className="badge">{contact.notificationStatus || '—'}</span></div>
          </div>
          <div>
            <div className="muted small">Token issued</div>
            <div className="small">{contact.tokenIssuedAt ? new Date(contact.tokenIssuedAt).toLocaleString() : '—'}</div>
          </div>
        </div>
        {isAdmin ? (
          <div className="row wrap" style={{ gap: 8 }}>
            <button className="btn secondary" onClick={() => resend(false)}>Resend notification</button>
            <button className="btn secondary" onClick={() => resend(true)}>Regenerate + resend</button>
            <button className="btn secondary" onClick={regen}>Regenerate token only</button>
            <button className="btn danger" onClick={forceComplete}>Force complete</button>
          </div>
        ) : null}
      </div>

      <div className="card">
        <h2 className="h2">Lenders</h2>
        {lenders.length === 0 ? <p className="muted">No flow rows yet — client hasn't landed on the page.</p> : (
          <table className="table">
            <thead>
              <tr><th>Lender</th><th>Status</th><th>Marked sent</th><th>BCC received</th><th></th></tr>
            </thead>
            <tbody>
              {lenders.map((l) => (
                <tr key={l.lenderName}>
                  <td>{l.lenderName}</td>
                  <td><span className={`badge ${l.status === 'bcc_received' ? 'success' : l.status === 'sent_by_client' ? 'primary' : ''}`}>{l.status}</span></td>
                  <td className="small">{l.clientMarkedSentAt ? new Date(l.clientMarkedSentAt).toLocaleString() : '—'}</td>
                  <td className="small">{l.bccReceivedAt ? new Date(l.bccReceivedAt).toLocaleString() : '—'}</td>
                  <td>{isAdmin && l.status !== 'bcc_received' && l.status !== 'sent_by_client' ? (
                    <button className="btn ghost small" onClick={() => markLender(l.lenderName)}>Mark sent</button>
                  ) : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2 className="h2">Notifications</h2>
        {notifications.length === 0 ? <p className="muted">No dispatches yet.</p> : (
          <table className="table">
            <thead>
              <tr><th>When</th><th>Type</th><th>Status</th><th>Triggered by</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {notifications.map((n) => (
                <tr key={n.dispatchId}>
                  <td className="small">{n.dispatchedAt ? new Date(n.dispatchedAt).toLocaleString() : '—'}</td>
                  <td>{n.dispatchType}</td>
                  <td><span className={`badge ${n.status === 'sent' ? 'success' : n.status.includes('fail') || n.status.includes('bounce') ? 'danger' : 'warn'}`}>{n.status}</span></td>
                  <td className="small muted">{n.triggeredBy}</td>
                  <td className="small muted">{n.failureReason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2 className="h2">Event timeline</h2>
        {events.length === 0 ? <p className="muted">No events yet.</p> : (
          <ul className="stack" style={{ listStyle: 'none', padding: 0 }}>
            {events.map((e) => (
              <li key={e.ROWID} className="small">
                <span className="muted">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span>
                {' · '}
                <strong>{e.eventType}</strong>
                {e.lenderName ? ` · ${e.lenderName}` : ''}
                {e.summary ? ` · ${e.summary}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
